import React, { useEffect, useState } from 'react';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import { useToast } from '../../contexts/ToastContext';
import {
  getDelhiveryWarehouse,
  createDelhiveryWarehouse,
  updateDelhiveryWarehouse,
  createDelhiveryPickup,
  getDelhiveryCharges
} from '../../services/orderService';
import '../../css/adminDelhiveryWarehouse.css';
import { Warehouse, Loader2, AlertCircle, CheckCircle2, Calculator, Truck, Pencil } from 'lucide-react';

const pad = (n) => String(n).padStart(2, '0');
const defaultPickupDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const defaultPickupTime = () => {
  const d = new Date();
  return `${pad(Math.min(23, d.getHours() + 1))}:00`;
};

const EMPTY_FORM = {
  name: '',
  registered_name: '',
  address: '',
  city: '',
  pin: '',
  phone: '',
  country: 'India',
  email: '',
  return_address: '',
  return_pin: '',
  return_city: '',
  return_state: '',
  return_country: 'India'
};

const AdminDelhiveryWarehousePage = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({
    configured: false,
    environment: 'test',
    pickupLocation: '',
    clientName: ''
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [sameReturn, setSameReturn] = useState(true);
  const [chargeForm, setChargeForm] = useState({
    d_pin: '',
    pt: 'Pre-paid',
    ss: 'Delivered',
    qty: '1'
  });
  const [chargeBusy, setChargeBusy] = useState(false);
  const [chargeResult, setChargeResult] = useState(null);
  const [pickupForm, setPickupForm] = useState({
    pickup_date: defaultPickupDate(),
    pickup_time: defaultPickupTime(),
    pickup_location: '',
    expected_package_count: '1'
  });
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupResult, setPickupResult] = useState(null);

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (sameReturn && ['address', 'city', 'pin'].includes(key)) {
        if (key === 'address') next.return_address = value;
        if (key === 'city') next.return_city = value;
        if (key === 'pin') next.return_pin = value;
      }
      return next;
    });
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getDelhiveryWarehouse();
        if (!active) return;
        setMeta({
          configured: Boolean(data.configured),
          environment: data.environment || 'test',
          pickupLocation: data.pickupLocation || '',
          clientName: data.clientName || ''
        });
        const defaults = data.defaults || {};
        setForm({
          ...EMPTY_FORM,
          ...defaults,
          country: defaults.country || 'India',
          return_country: defaults.return_country || 'India'
        });
        setPickupForm((prev) => ({
          ...prev,
          pickup_location: data.pickupLocation || defaults.name || prev.pickup_location
        }));
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || err.message || 'Could not load warehouse defaults');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const onSameReturn = (checked) => {
    setSameReturn(checked);
    if (checked) {
      setForm((prev) => ({
        ...prev,
        return_address: prev.address,
        return_city: prev.city,
        return_pin: prev.pin,
        return_country: prev.country || 'India'
      }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = sameReturn
        ? {
            ...form,
            return_address: form.address,
            return_city: form.city,
            return_pin: form.pin,
            return_country: form.country || 'India'
          }
        : form;
      const result = await createDelhiveryWarehouse(payload);
      setMeta((prev) => ({
        ...prev,
        pickupLocation: result.saved?.pickupLocation || form.name,
        clientName: result.saved?.clientName || form.registered_name
      }));
      setPickupForm((prev) => ({
        ...prev,
        pickup_location: result.saved?.pickupLocation || form.name
      }));
      addToast(result.message || 'Warehouse registered with Delhivery');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Delhivery rejected the warehouse';
      setError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const onUpdate = async () => {
    setError('');
    setUpdating(true);
    try {
      const result = await updateDelhiveryWarehouse({
        name: meta.pickupLocation || form.name,
        pin: form.pin,
        address: form.address,
        phone: form.phone
      });
      addToast(result.message || 'Warehouse updated on Delhivery');
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Delhivery rejected the warehouse update';
      setError(message);
      addToast(message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Courier Warehouse" />

        <div className="adw-page">
          <div className="adw-head">
            <div>
              <h1>Delhivery warehouse</h1>
              <p>
                Register the pickup location Delhivery will collect from. The warehouse name is
                case-sensitive and must match exactly when you ship an order.
              </p>
            </div>
          </div>

          <div className="adw-status">
            <div className={`adw-pill ${meta.configured ? 'ok' : 'warn'}`}>
              {meta.configured ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              Token {meta.configured ? 'configured' : 'missing'}
            </div>
            <div className="adw-pill">
              Environment: {meta.environment === 'production' ? 'Production' : 'Test'}
            </div>
            <div className={`adw-pill ${meta.pickupLocation ? 'ok' : 'warn'}`}>
              Pickup: {meta.pickupLocation || 'not set'}
            </div>
            <div className={`adw-pill ${meta.clientName ? 'ok' : 'warn'}`}>
              Client: {meta.clientName || 'not set'}
            </div>
          </div>

          {loading ? (
            <p className="adw-muted">
              <Loader2 size={16} className="adw-spin" /> Loading seller defaults…
            </p>
          ) : (
            <form className="adw-card" onSubmit={onSubmit}>
              <div className="adw-card-head">
                <Warehouse size={16} />
                <h2>Client warehouse details</h2>
              </div>

              {!meta.configured && (
                <div className="adw-alert">
                  <AlertCircle size={16} />
                  Add the Delhivery API token in the server .env before registering a warehouse.
                </div>
              )}

              {error && (
                <div className="adw-alert">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <div className="adw-grid">
                <label>
                  Warehouse name
                  <input
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="Mithralaya WH"
                    required
                    readOnly={Boolean(meta.pickupLocation)}
                  />
                </label>
                <label>
                  Client / HQ name
                  <input
                    value={form.registered_name}
                    onChange={(e) => setField('registered_name', e.target.value)}
                    placeholder="Registered account name (optional)"
                  />
                </label>
                <label className="adw-span-2">
                  Pickup address
                  <input
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                  />
                </label>
                <label>
                  City
                  <input value={form.city} onChange={(e) => setField('city', e.target.value)} />
                </label>
                <label>
                  Pincode
                  <input
                    value={form.pin}
                    onChange={(e) => setField('pin', e.target.value)}
                    inputMode="numeric"
                    maxLength={6}
                    required
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    inputMode="numeric"
                    maxLength={10}
                    required
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                  />
                </label>
                <label>
                  Country
                  <input value={form.country} onChange={(e) => setField('country', e.target.value)} />
                </label>
              </div>

              <label className="adw-check">
                <input
                  type="checkbox"
                  checked={sameReturn}
                  onChange={(e) => onSameReturn(e.target.checked)}
                />
                Return address is the same as pickup
              </label>

              {!sameReturn && (
                <div className="adw-grid">
                  <label className="adw-span-2">
                    Return address
                    <input
                      value={form.return_address}
                      onChange={(e) => setField('return_address', e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Return city
                    <input
                      value={form.return_city}
                      onChange={(e) => setField('return_city', e.target.value)}
                    />
                  </label>
                  <label>
                    Return state
                    <input
                      value={form.return_state}
                      onChange={(e) => setField('return_state', e.target.value)}
                    />
                  </label>
                  <label>
                    Return pincode
                    <input
                      value={form.return_pin}
                      onChange={(e) => setField('return_pin', e.target.value)}
                      inputMode="numeric"
                      maxLength={6}
                    />
                  </label>
                  <label>
                    Return country
                    <input
                      value={form.return_country}
                      onChange={(e) => setField('return_country', e.target.value)}
                    />
                  </label>
                </div>
              )}

              {sameReturn && (
                <label>
                  Return state
                  <input
                    value={form.return_state}
                    onChange={(e) => setField('return_state', e.target.value)}
                    placeholder="Tamil Nadu"
                  />
                </label>
              )}

              <p className="adw-help">
                Warehouse name cannot be changed after it is registered. Update only address, pin,
                and phone.
              </p>
              <div className="adw-actions">
                <button type="submit" className="adw-btn" disabled={saving || updating || !meta.configured}>
                  {saving ? <Loader2 size={16} className="adw-spin" /> : <Warehouse size={16} />}
                  {saving ? 'Registering…' : 'Register with Delhivery'}
                </button>
                <button
                  type="button"
                  className="adw-btn"
                  disabled={saving || updating || !meta.configured}
                  onClick={onUpdate}
                >
                  {updating ? <Loader2 size={16} className="adw-spin" /> : <Pencil size={16} />}
                  {updating ? 'Updating…' : 'Update warehouse'}
                </button>
              </div>
            </form>
          )}

          <form
            className="adw-card"
            onSubmit={async (e) => {
              e.preventDefault();
              setPickupBusy(true);
              setPickupResult(null);
              try {
                const data = await createDelhiveryPickup({
                  pickup_date: pickupForm.pickup_date,
                  pickup_time: pickupForm.pickup_time,
                  pickup_location: pickupForm.pickup_location,
                  expected_package_count: Number(pickupForm.expected_package_count)
                });
                setPickupResult(data);
                addToast(data.message || `Pickup request ${data.pickupId} created`);
              } catch (err) {
                addToast(err.response?.data?.message || err.message || 'Could not create pickup request', 'error');
              } finally {
                setPickupBusy(false);
              }
            }}
          >
            <div className="adw-card-head">
              <Truck size={16} />
              <h2>Request pickup</h2>
            </div>
            <p className="adw-help">
              Pickup is raised against the warehouse, not each waybill. One open request per
              location per day. Raise it after shipments are packed and labelled.
            </p>
            <div className="adw-grid">
              <label>
                Warehouse name
                <input
                  value={pickupForm.pickup_location}
                  onChange={(e) => setPickupForm((f) => ({ ...f, pickup_location: e.target.value }))}
                  required
                />
              </label>
              <label>
                Package count
                <input
                  value={pickupForm.expected_package_count}
                  onChange={(e) => setPickupForm((f) => ({
                    ...f,
                    expected_package_count: e.target.value.replace(/\D/g, '').slice(0, 4)
                  }))}
                  inputMode="numeric"
                  required
                />
              </label>
              <label>
                Pickup date
                <input
                  type="date"
                  value={pickupForm.pickup_date}
                  onChange={(e) => setPickupForm((f) => ({ ...f, pickup_date: e.target.value }))}
                  required
                />
              </label>
              <label>
                Pickup time
                <input
                  type="time"
                  step="1"
                  value={pickupForm.pickup_time}
                  onChange={(e) => setPickupForm((f) => ({ ...f, pickup_time: e.target.value }))}
                  required
                />
              </label>
            </div>
            <div className="adw-actions">
              <button type="submit" className="adw-btn" disabled={pickupBusy || !meta.configured}>
                {pickupBusy ? <Loader2 size={16} className="adw-spin" /> : <Truck size={16} />}
                {pickupBusy ? 'Requesting…' : 'Create pickup request'}
              </button>
              {pickupResult?.pickupId && (
                <span className="adw-charge">Pickup #{pickupResult.pickupId}</span>
              )}
            </div>
          </form>

          <form
            className="adw-card"
            onSubmit={async (e) => {
              e.preventDefault();
              setChargeBusy(true);
              setChargeResult(null);
              try {
                const data = await getDelhiveryCharges({
                  d_pin: chargeForm.d_pin,
                  pt: chargeForm.pt,
                  ss: chargeForm.ss,
                  qty: chargeForm.qty
                });
                setChargeResult(data);
              } catch (err) {
                addToast(err.response?.data?.message || err.message || 'Could not calculate charges', 'error');
              } finally {
                setChargeBusy(false);
              }
            }}
          >
            <div className="adw-card-head">
              <Calculator size={16} />
              <h2>Estimate shipping cost</h2>
            </div>
            <p className="adw-help">
              Delhivery returns an approximate charge. Values can change at billing.
            </p>
            <div className="adw-grid">
              <label>
                Destination pincode
                <input
                  value={chargeForm.d_pin}
                  onChange={(e) => setChargeForm((f) => ({ ...f, d_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  inputMode="numeric"
                  maxLength={6}
                  required
                />
              </label>
              <label>
                Payment type
                <select
                  value={chargeForm.pt}
                  onChange={(e) => setChargeForm((f) => ({ ...f, pt: e.target.value }))}
                >
                  <option value="Pre-paid">Prepaid</option>
                  <option value="COD">COD</option>
                </select>
              </label>
              <label>
                Shipment status
                <select
                  value={chargeForm.ss}
                  onChange={(e) => setChargeForm((f) => ({ ...f, ss: e.target.value }))}
                >
                  <option value="Delivered">Delivered</option>
                  <option value="RTO">RTO</option>
                  <option value="DTO">DTO</option>
                </select>
              </label>
              <label>
                Item quantity
                <input
                  value={chargeForm.qty}
                  onChange={(e) => setChargeForm((f) => ({ ...f, qty: e.target.value }))}
                  inputMode="numeric"
                />
              </label>
            </div>
            <div className="adw-actions">
              <button type="submit" className="adw-btn" disabled={chargeBusy || !meta.configured}>
                {chargeBusy ? <Loader2 size={16} className="adw-spin" /> : <Calculator size={16} />}
                {chargeBusy ? 'Calculating…' : 'Calculate'}
              </button>
              {chargeResult?.amount != null && (
                <span className="adw-charge">Approx. ₹{chargeResult.amount}</span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminDelhiveryWarehousePage;
